import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  DashboardCubit,
  DashboardState,
  DashboardStateType,
  Loading,
  MyopiaProgressionDisplay,
  NoCalcDisplay,
  PremyopeDisplay,
  SingleMyopeDisplay,
} from './dashboard.cubit';
import { Subscription } from 'rxjs';
import { CalculationResults } from '../backend-response-models/calc-interfaces.helper';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { COMMON_IMPORTS } from '../common-imports';
import { MyopiaSingleDashboardComponent } from '../myopia-single-dashboard/myopia-single-dashboard.component';
import { DashboardBarComponent } from '../dashboard-bar/dashboard-bar.component';
import { Language } from '../shared/shared-interfaces/shared.interfaces';
import { ReportType } from '../shared/shared-enums/shared.enums';
import { PremyopiaDashboardComponent } from '../premyopia-dashboard/premyopia-dashboard.component';
import { MyopiaProgressionDashboardComponent } from '../myopia-progression-dashboard/myopia-progression-dashboard.component';
import { NoCalcDashboardComponent } from '../no-calc-dashboard/no-calc-dashboard.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  standalone: true,
  imports: [
    ProgressSpinnerModule,
    MyopiaSingleDashboardComponent,
    DashboardBarComponent,
    ...COMMON_IMPORTS,
    PremyopiaDashboardComponent,
    MyopiaProgressionDashboardComponent,
    NoCalcDashboardComponent,
  ],
  providers: [DashboardCubit],
  styleUrls: ['./dashboard.component.css'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  DashboardStateType = DashboardStateType;
  SingleMyopeDisplay = SingleMyopeDisplay;
  ReportType = ReportType;
  uiStateSubscription?: Subscription;

  currentDashboardState: DashboardState = new Loading();
  calcResults?: CalculationResults | null;
  reportType: ReportType = ReportType.ECP;
  hideMenu: boolean = false;
  useInternalAPI: boolean = false;
  patientName: string = 'anonymous';

  constructor(
    private route: ActivatedRoute,
    private dashboardCubit: DashboardCubit
  ) {}

  ngOnInit(): void {
    this.subscribeToUIState();

    const dashboardID = this.route.snapshot.paramMap.get('id');
    const bearerToken = this.route.snapshot.queryParamMap.get('token');
    const hideMenuAsStr = this.route.snapshot.queryParamMap.get('hide-menu');
    const languageAsStr = this.route.snapshot.queryParamMap.get('language');
    const reportTypeAsStr = this.route.snapshot.queryParamMap.get('report-type');
    const name = this.route.snapshot.queryParamMap.get('name');
    this.patientName = name ? decodeURIComponent(name) : 'anonymous';
    const useInternalAPIAsStr = this.route.snapshot.queryParamMap.get('use-internal-api');
    //url.searchParams.append('use-internal-api', 'true');

    if (reportTypeAsStr && reportTypeAsStr === 'parent') {
      this.reportType = ReportType.Parent;
    } else if (reportTypeAsStr && reportTypeAsStr === 'ecp') {
      this.reportType = ReportType.ECP;
    } else {
      this.reportType = ReportType.ECP;
    }

    this.useInternalAPI = useInternalAPIAsStr === 'true';
    this.hideMenu = hideMenuAsStr === 'true';
    console.log('use internal api', this.useInternalAPI);
    this.dashboardCubit.begin(bearerToken, dashboardID, this.useInternalAPI, this.reportType);
  }

  handleUIStateChange(newState: DashboardState): void {
    this.currentDashboardState = newState;
    this.calcResults = null;

    console.log('New state:', newState);

    if (newState instanceof Loading) {
      return;
    }

    if (newState instanceof SingleMyopeDisplay) {
      this.calcResults = newState.calcResults;
      this.reportType = newState.reportType;
      return;
    }

    if (newState instanceof MyopiaProgressionDisplay) {
      this.calcResults = newState.calcResults;
      this.reportType = newState.reportType;
      return;
    }

    if (newState instanceof PremyopeDisplay) {
      this.calcResults = newState.calcResults;
      this.reportType = newState.reportType;
      return;
    }

    if (newState instanceof NoCalcDisplay) {
      this.calcResults = newState.calcResults;
      this.reportType = newState.reportType;
      return;
    }
  }

  reportTypeDidChange(reportType: ReportType): void {
    this.dashboardCubit.reportTypeDidChange(reportType);
  }

  languageDidChange(language: Language): void {
    this.dashboardCubit.languageDidChange(language);
  }

  subscribeToUIState(): void {
    this.uiStateSubscription = this.dashboardCubit.uiStateChanged.subscribe(state => this.handleUIStateChange(state));
  }

  unsubscribeFromUIState(): void {
    this.uiStateSubscription?.unsubscribe();
    this.uiStateSubscription = undefined;
  }

  downloadPressed(): void {
    this.dashboardCubit.downloadPressed();
  }

  ngOnDestroy(): void {
    this.unsubscribeFromUIState();
  }
}
